/**
 * Браузерное хранилище: все ключи и всё чтение-запись в одном месте.
 *
 * Раньше каждый экран сам звал `localStorage.getItem` и сам разбирал строку
 * в число — семь разных ключей по пяти файлам. Теперь ключи перечислены здесь
 * и видно, что вообще запоминает интерфейс.
 *
 * `local` переживает закрытие вкладки, `session` живёт до её конца. Сейчас в
 * session не лежит ничего: признак «база сайта отвечала» ушёл вместе с
 * запросами в базу сайта через Россию.
 */

export const StorageKey = {
	token: "riga.token",
	language: "riga.languageId",

	publishSite: "riga.publish.site",
	publishCatalog: "riga.publish.catalog",

	dictionaryCatalog: "riga.dictionary.catalog",
	dictionarySource: "riga.dictionary.source",
	dictionarySite: "riga.dictionary.site",
	dictionarySymbols: "riga.dictionary.symbols",
	dictionaryLinks: "riga.dictionary.links",

	groupsScope: "riga.groups.scope",
	groupsBrand: "riga.groups.brand",
	groupsSearch: "riga.groups.search",

	pdfCatalog: "riga.pdf.catalog",
	pdfWebsite: "riga.pdf.website",
	pdfImages: "riga.pdf.images",
	pdfToc: "riga.pdf.toc",
	pdfIndex: "riga.pdf.index",
	pdfLayout: "riga.pdf.layout",

	guidesSpeed: "riga.guides.speed",
} as const

export type StorageKeyName = (typeof StorageKey)[keyof typeof StorageKey]

/** Хранилище может быть недоступно (приватный режим, запрет на куки) —
 *  интерфейс от этого падать не должен, просто ничего не запомнит. */
function safe<T>(action: () => T, fallback: T): T {
	try {
		return action()
	} catch {
		return fallback
	}
}

function reader(store: () => Storage) {
	return {
		text: (key: StorageKeyName): string | null =>
			safe(() => store().getItem(key), null),

		number: (key: StorageKeyName): number | null => {
			const raw = safe(() => store().getItem(key), null)
			if (raw === null) return null
			const value = Number(raw)
			return Number.isFinite(value) ? value : null
		},

		flag: (key: StorageKeyName): boolean =>
			safe(() => store().getItem(key), null) === "1",

		json: <T>(key: StorageKeyName, fallback: T): T => {
			const raw = safe(() => store().getItem(key), null)
			if (raw === null) return fallback
			return safe(() => JSON.parse(raw) as T, fallback)
		},

		write: (key: StorageKeyName, value: string | number | boolean | null): void => {
			safe(() => {
				if (value === null) store().removeItem(key)
				else if (typeof value === "boolean") store().setItem(key, value ? "1" : "0")
				else store().setItem(key, String(value))
			}, undefined)
		},

		writeJson: (key: StorageKeyName, value: unknown): void => {
			safe(() => store().setItem(key, JSON.stringify(value)), undefined)
		},

		remove: (key: StorageKeyName): void => {
			safe(() => store().removeItem(key), undefined)
		},
	}
}

export const local = reader(() => window.localStorage)
export const session = reader(() => window.sessionStorage)
